import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Button, Form, FormInstance, FormProps } from 'antd'
import debounce from 'lodash/debounce'
import { getLabelForAssessmentType } from 'components/Patients/Assessments/helpers'
import { getCssSelectorForLinkId } from 'utils/helpers'
import { AdapterRegistry } from 'components/Assessments/adapters/AdapterRegistry'
import { useStore } from 'store'
import useAutosaveOnChange from 'hooks/useAutosaveOnChange'
import { useUpdateAssessmentUncontrolled } from 'components/Assessments/hooks/useUpdateAssessment'
import { SharedFormField } from 'components/Assessments/types/SharedFormField'
import { sanitizeInput } from 'utils/inputSanitization'
import { useAssessmentContext } from './AssessmentContext'
import AssessmentLayoutContext from './AssessmentLayoutContext'
import { NotificationContext, NOTIFY_KEYS } from './NotificationContext'
import PatientContext from './PatientContext'

interface AssessmentFormContextType {
  form?: FormInstance
  triggerAutosave?: () => void
}

export type DayToCaregiverIds = Record<string, string[]>

export interface CustomAssessmentFormErrors {
  missingInterventions?: boolean
  schedulingConflicts?: DayToCaregiverIds
}

const AssessmentFormContext = createContext<AssessmentFormContextType>({
  form: undefined
})

// Constants for error container IDs
const INTERVENTIONS_ERRORS_LIST = 'interventions-errors'
const DAILY_VISIT_SCHEDULE_ERROR_CONTAINER = 'daily-visit-schedule-errors'
const WEEKLY_VISIT_SCHEDULE_ERROR_CONTAINER = 'weekly-visit-schedule-errors'

/**
 * Creates a form wrapper that overrides setFieldValue to sanitize string inputs.
 * This ensures all string values set via setFieldValue are sanitized according to FHIR requirements.
 *
 * @param form - The original Ant Design form instance
 * @returns A new form instance with sanitized setFieldValue method
 */
const createSanitizedFormInstance = (form: FormInstance): FormInstance => {
  return {
    ...form,
    setFieldValue: (name: Parameters<FormInstance['setFieldValue']>[0], value: unknown) => {
      // Sanitize string values before setting them
      const sanitizedValue = typeof value === 'string' ? sanitizeInput(value) : value
      return form.setFieldValue(name, sanitizedValue)
    }
  } as FormInstance
}

interface AssessmentFormProviderProps {
  name: string
  children: React.ReactNode
  onFinish: () => void
  uncontrolledSections?: Set<string>
}

export const AssessmentUncontrolledFormProvider = ({
  name,
  children,
  onFinish,
  uncontrolledSections
}: AssessmentFormProviderProps): JSX.Element => {
  const [form] = Form.useForm()
  const sanitizedForm = useMemo(() => createSanitizedFormInstance(form), [form])
  const [shouldAutoValidate, setShouldAutoValidate] = useState<boolean>(false)

  const { setOnAssessmentCompleteClick } = useContext(AssessmentLayoutContext)
  const { assessmentType, cantEditAssessmentFields, response } = useAssessmentContext()
  const { notify } = useContext(NotificationContext)

  // Get care team data for fallback initial values
  const { patientId } = useContext(PatientContext)
  const { idToPatientMapping } = useStore((state) => state.patient)
  const { byId: byCareTeamId } = useStore((state) => state.careTeam)
  const careTeamId = patientId ? idToPatientMapping[patientId]?.careTeamId : undefined
  const careTeam = careTeamId ? byCareTeamId[careTeamId] : null
  const caregiverTimezone = patientId ? idToPatientMapping[patientId]?.caregiverTimezone : undefined

  // Setup autosave for uncontrolled sections
  const updateAssessmentUncontrolled = useUpdateAssessmentUncontrolled()
  const autosaveHandler = useAutosaveOnChange(updateAssessmentUncontrolled, { response }, 5000)

  useEffect(() => {
    setOnAssessmentCompleteClick(() => form.submit)
  }, [form, setOnAssessmentCompleteClick])

  const scrollToFirstError = useCallback((): void => {
    const fieldWithError = form.getFieldsError().find(({ errors }) => errors.length > 0)
    if (!fieldWithError?.name) return

    // Handle both string and array field names properly
    const fieldName = Array.isArray(fieldWithError.name)
      ? fieldWithError.name.join('.')
      : String(fieldWithError.name)

    // Guard against empty field names to prevent malformed regex patterns
    if (!fieldName.trim()) return

    // Try Ant Design's built-in scroll mechanism first
    form.scrollToField(fieldWithError.name, {
      behavior: 'smooth',
      block: 'center'
    })

    // Fallback mechanism: Only run if Ant Design's scrollToField() fails to scroll properly
    // Using requestAnimationFrame for better timing with browser rendering
    requestAnimationFrame(() => {
      // Check if Ant Design's scroll worked by finding if the target element is in viewport
      // This prevents expensive DOM searches when scrolling already succeeded
      const antdElement =
        document.querySelector(`[data-field-name="${fieldName}"]`) ||
        document.querySelector(`[name="${fieldName}"]`)

      if (antdElement) {
        const rect = antdElement.getBoundingClientRect()
        const isInViewport =
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)

        // If Ant Design's scroll worked, we're done
        if (isInViewport) return
      }

      // Step 1: Try common ID patterns used across different assessment types
      // Our forms use various naming conventions depending on the field type and assessment
      const idPatterns = [
        // Pattern 1: Transformed CSS selector - converts slashes to underscores
        // Example: 'plan-of-care/interventions' becomes 'plan-of-care_interventions'
        getCssSelectorForLinkId(fieldName),
        // Pattern 2: Raw field name - some simpler fields use the exact field name as ID
        fieldName
      ]

      let element: HTMLElement | null = null

      // Attempt to find the element using the common patterns first
      for (const pattern of idPatterns) {
        element = document.getElementById(pattern)
        if (element) break
      }

      // Special handling for ADL fields - try ADL-specific selector as fallback
      if (!element && fieldName.startsWith('adlNeeds.')) {
        element = document.querySelector(
          `[data-testid*="-card"] .ant-form-item-has-error`
        ) as HTMLElement
      }

      // Special handling for interventions field - Form.List errors are displayed via Form.ErrorList
      if (!element && fieldName === SharedFormField.INTERVENTIONS) {
        // Try to find the interventions error container using id
        const interventionsErrorContainer = document.getElementById(INTERVENTIONS_ERRORS_LIST)
        if (interventionsErrorContainer) {
          element = interventionsErrorContainer
        }
      }

      // Special handling for weekly visit schedule field
      if (
        !element &&
        fieldName === SharedFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_BY_WEEKDAY
      ) {
        // Try to find the weekly visit schedule error container using id
        const weeklyScheduleErrorContainer = document.getElementById(
          WEEKLY_VISIT_SCHEDULE_ERROR_CONTAINER
        )
        if (weeklyScheduleErrorContainer) {
          element = weeklyScheduleErrorContainer
        }
      }

      // Special handling for daily visit schedule fields
      if (
        !element &&
        (fieldName === SharedFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VALID ||
          fieldName === SharedFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS ||
          fieldName === SharedFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_EDITED_VISITS)
      ) {
        // Try to find the daily visit schedule error container using id
        const dailyScheduleErrorContainer = document.getElementById(
          DAILY_VISIT_SCHEDULE_ERROR_CONTAINER
        )
        if (dailyScheduleErrorContainer) {
          element = dailyScheduleErrorContainer
        }
      }

      // Step 2: If common patterns fail, use regex to find assessment-specific prefixed IDs
      // Many assessment fields have IDs like: "soc_{fieldName}_help", "pat_{fieldName}_help", etc.
      if (!element) {
        // Escape special regex characters in field name
        const escapedFieldName = fieldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        // Match any prefix followed by underscore, field name, underscore, and "help"
        const regexPattern = new RegExp(`^.+_${escapedFieldName}_help$`)

        // Search through ALL elements with IDs
        const allElements = Array.from(document.querySelectorAll('[id]'))
        for (const el of allElements) {
          if (regexPattern.test(el.id)) {
            element = el as HTMLElement
            break
          }
        }
      }

      // Step 3: If we found the element, check if it needs scrolling
      if (element) {
        // Verify if the element is actually visible in the current viewport
        // getBoundingClientRect() gives us the element's position relative to the viewport
        const rect = element.getBoundingClientRect()
        const isInViewport =
          rect.top >= 0 && // Not above the viewport
          rect.left >= 0 && // Not to the left of the viewport
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && // Not below the viewport
          rect.right <= (window.innerWidth || document.documentElement.clientWidth) // Not to the right of the viewport

        // Only scroll if the element is not already visible to avoid unnecessary movement
        if (!isInViewport) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
      }
    }) // requestAnimationFrame ensures execution after next browser paint cycle
  }, [form])

  const onValidationFinish = useCallback(
    (fieldErrors: unknown[]) => {
      const hasErrors = fieldErrors.length > 0
      setShouldAutoValidate(true)

      if (!hasErrors) {
        notify.destroy(NOTIFY_KEYS.ASSESSMENT_VALIDATION_ERROR)
      } else {
        const assessmentTypeLabel = assessmentType
          ? getLabelForAssessmentType(assessmentType)
          : null

        const goToErrorButton = (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              scrollToFirstError()
            }}
          >
            Go to error
          </Button>
        )

        notify.error({
          message: `Unable to Complete ${assessmentTypeLabel ?? 'Assessment'}`,
          description: `Please resolve ${fieldErrors.length} error(s) before submitting`,
          btn: goToErrorButton,
          key: NOTIFY_KEYS.ASSESSMENT_VALIDATION_ERROR,
          duration: null
        })
      }
    },
    [assessmentType, notify, scrollToFirstError]
  )

  const queueFormValidation = useMemo(
    () =>
      debounce(async () => {
        if (shouldAutoValidate) {
          let fieldErrors: unknown[] = []
          try {
            await form.validateFields()
          } catch (error) {
            fieldErrors = error.errorFields
          } finally {
            onValidationFinish(fieldErrors)
          }
        }
      }, 500),
    [form, onValidationFinish, shouldAutoValidate]
  )

  // Compose validation and autosave handlers
  const handleValuesChange = useCallback(
    (changedValues: Record<string, unknown>, allValues: Record<string, unknown>) => {
      // Only run autosave if feature flag is enabled and uncontrolled sections exist
      autosaveHandler(changedValues, allValues)

      void queueFormValidation()
    },
    [queueFormValidation, autosaveHandler]
  )

  // Expose a function to manually trigger autosave (e.g., after programmatic setFieldsValue)
  const triggerAutosave = useCallback(() => {
    if (form) {
      const allValues = form.getFieldsValue()
      handleValuesChange({}, allValues)
    }
  }, [form, handleValuesChange])

  const onFinishSuccessHandler = (): void => {
    notify.destroy(NOTIFY_KEYS.ASSESSMENT_VALIDATION_ERROR)
    onFinish()
  }

  const onFinishFailedHandler: FormProps['onFinishFailed'] = ({ errorFields }): void => {
    onValidationFinish(errorFields)
  }

  // Convert flatAnswers to initial form values using adapter pattern
  // Only load values for uncontrolled sections to avoid interfering with controlled sections
  const initialValues = useMemo(() => {
    if (!assessmentType || !response) return {}

    const adapter = AdapterRegistry.getAdapter(assessmentType)
    return adapter.toForm(response, {
      uncontrolledSections,
      careTeam: careTeam || undefined,
      caregiverTimezone: caregiverTimezone ?? ''
    })
  }, [assessmentType, response, uncontrolledSections, careTeam, caregiverTimezone])

  return (
    <Form
      name={name}
      form={form}
      onFinish={onFinishSuccessHandler}
      onFinishFailed={onFinishFailedHandler}
      disabled={cantEditAssessmentFields}
      scrollToFirstError={{
        behavior: 'smooth',
        block: 'center'
      }}
      onValuesChange={handleValuesChange}
      initialValues={initialValues}
      noValidate
    >
      <AssessmentFormContext.Provider value={{ form: sanitizedForm, triggerAutosave }}>
        {children}
      </AssessmentFormContext.Provider>
    </Form>
  )
}

export const AssessmentControlledFormProvider = ({
  name,
  children,
  onFinish
}: AssessmentFormProviderProps): JSX.Element => {
  const [form] = Form.useForm()
  const sanitizedForm = useMemo(() => createSanitizedFormInstance(form), [form])
  const [totalErrors, setTotalErrors] = useState<number>(0)

  const { flatAnswers, response } = useAssessmentContext()
  const { setOnAssessmentCompleteClick } = useContext(AssessmentLayoutContext)
  const { assessmentType, cantEditAssessmentFields } = useAssessmentContext()
  const { notify } = useContext(NotificationContext)

  useEffect(() => {
    setOnAssessmentCompleteClick(() => form.submit)
  }, [form, setOnAssessmentCompleteClick])

  const scrollToFirstError = useCallback((): void => {
    const fieldWithError = form.getFieldsError().find(({ errors }) => errors.length > 0)
    if (!fieldWithError?.name) return

    // Handle both string and array field names properly
    const fieldName = Array.isArray(fieldWithError.name)
      ? fieldWithError.name.join('.')
      : String(fieldWithError.name)

    // Guard against empty field names to prevent malformed regex patterns
    if (!fieldName.trim()) return

    // Try Ant Design's built-in scroll mechanism first
    form.scrollToField(fieldWithError.name, {
      behavior: 'smooth',
      block: 'center'
    })

    // Fallback mechanism: Only run if Ant Design's scrollToField() fails to scroll properly
    // Using requestAnimationFrame for better timing with browser rendering
    requestAnimationFrame(() => {
      // Check if Ant Design's scroll worked by finding if the target element is in viewport
      // This prevents expensive DOM searches when scrolling already succeeded
      const antdElement =
        document.querySelector(`[data-field-name="${fieldName}"]`) ||
        document.querySelector(`[name="${fieldName}"]`)

      if (antdElement) {
        const rect = antdElement.getBoundingClientRect()
        const isInViewport =
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)

        // If Ant Design's scroll worked, we're done
        if (isInViewport) return
      }

      // Step 1: Try common ID patterns used across different assessment types
      // Our forms use various naming conventions depending on the field type and assessment
      const idPatterns = [
        // Pattern 1: Transformed CSS selector - converts slashes to underscores
        // Example: 'plan-of-care/interventions' becomes 'plan-of-care_interventions'
        getCssSelectorForLinkId(fieldName),
        // Pattern 2: Raw field name - some simpler fields use the exact field name as ID
        fieldName
      ]

      let element: HTMLElement | null = null

      // Attempt to find the element using the common patterns first
      for (const pattern of idPatterns) {
        element = document.getElementById(pattern)
        if (element) break
      }

      // Special handling for weekly visit schedule field
      if (!element && fieldName === 'visit-scheduling/custom-schedule/weekday-visits') {
        // Try to find the weekly visit schedule error container using id
        const weeklyScheduleErrorContainer = document.getElementById('weekly-visit-schedule-errors')
        if (weeklyScheduleErrorContainer) {
          element = weeklyScheduleErrorContainer
        }
      }

      // Special handling for daily visit schedule fields
      if (
        !element &&
        (fieldName === 'visit-scheduling/custom-schedule/valid' ||
          fieldName === 'visit-scheduling/custom-schedule/visits' ||
          fieldName === 'visit-scheduling/custom-schedule/edited-visits')
      ) {
        // Try to find the daily visit schedule error container using id
        const dailyScheduleErrorContainer = document.getElementById('daily-visit-schedule-errors')
        if (dailyScheduleErrorContainer) {
          element = dailyScheduleErrorContainer
        }
      }

      // Step 2: If common patterns fail, use regex to find assessment-specific prefixed IDs
      // Many assessment fields have IDs like: "soc_{fieldName}_help", "pat_{fieldName}_help", etc.
      if (!element) {
        // Escape special regex characters in field name
        const escapedFieldName = fieldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        // Match any prefix followed by underscore, field name, underscore, and "help"
        const regexPattern = new RegExp(`^.+_${escapedFieldName}_help$`)

        // Search through ALL elements with IDs
        const allElements = Array.from(document.querySelectorAll('[id]'))
        for (const el of allElements) {
          if (regexPattern.test(el.id)) {
            element = el as HTMLElement
            break
          }
        }
      }

      // Step 3: If we found the element, check if it needs scrolling
      if (element) {
        // Verify if the element is actually visible in the current viewport
        // getBoundingClientRect() gives us the element's position relative to the viewport
        const rect = element.getBoundingClientRect()
        const isInViewport =
          rect.top >= 0 && // Not above the viewport
          rect.left >= 0 && // Not to the left of the viewport
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && // Not below the viewport
          rect.right <= (window.innerWidth || document.documentElement.clientWidth) // Not to the right of the viewport

        // Only scroll if the element is not already visible to avoid unnecessary movement
        if (!isInViewport) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
      }
    }) // requestAnimationFrame ensures execution after next browser paint cycle
  }, [form])

  const validateFields = useMemo(() => {
    return debounce(async (): Promise<void> => {
      try {
        await form.validateFields()
        notify.destroy(NOTIFY_KEYS.ASSESSMENT_VALIDATION_ERROR)
        setTotalErrors(0) // Clear errors if validation passes
      } catch (e) {
        setTotalErrors(e.errorFields.length)

        const assessmentTypeLabel = assessmentType
          ? getLabelForAssessmentType(assessmentType)
          : null

        const goToErrorButton = (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              scrollToFirstError()
            }}
          >
            Go to error
          </Button>
        )

        notify.error({
          message: `Unable to Complete ${assessmentTypeLabel ?? 'Assessment'}`,
          description: `Please resolve ${e.errorFields.length} error(s) before submitting`,
          btn: goToErrorButton,
          key: NOTIFY_KEYS.ASSESSMENT_VALIDATION_ERROR,
          duration: null
        })
      }
    }, 500) // Debounce with a 500ms delay
  }, [form, notify, assessmentType, scrollToFirstError])

  useEffect(() => {
    if (totalErrors > 0) {
      // Auto validates fields every time answers changes
      // Watch both response and flatAnswers to catch updates in all assessment types
      void validateFields()
    }
  }, [response, flatAnswers, totalErrors, validateFields])

  useEffect(() => {
    return () => {
      validateFields.cancel() // Cancel any pending debounced calls
    }
  }, [flatAnswers, totalErrors, validateFields])

  const onFinishHandler = (): void => {
    notify.destroy(NOTIFY_KEYS.ASSESSMENT_VALIDATION_ERROR)
    onFinish()
  }

  const onFormFinishFailed: FormProps['onFinishFailed'] = ({ errorFields }): void => {
    setTotalErrors(errorFields.length)
  }

  return (
    <Form
      name={name}
      form={form}
      onFinish={onFinishHandler}
      onFinishFailed={onFormFinishFailed}
      disabled={cantEditAssessmentFields}
      scrollToFirstError={{
        behavior: 'smooth',
        block: 'center'
      }}
    >
      <AssessmentFormContext.Provider value={{ form: sanitizedForm }}>
        {children}
      </AssessmentFormContext.Provider>
    </Form>
  )
}

export default AssessmentFormContext
